import Decimal from 'decimal.js';
import { http, HttpResponse, type HttpHandler } from 'msw';

/**
 * DEV-ONLY mock of the cards module.
 *
 * Mirrors `src/modules/Novix.Modules.Cards/Endpoints/CardEndpoints.cs` route for
 * route, including the failure paths, so the flows can be walked end to end
 * without a backend and without a partner.
 *
 * Deliberately stateful and deliberately slow in the right places:
 *
 *   - a newly issued card sits in `Issuing` for 20 seconds before it becomes
 *     `Active`, so "accepted is not success" is something you can watch rather
 *     than something you have to take on trust;
 *   - a closed card sits in `Closing` for 20 seconds and then settles to one of
 *     three refund outcomes — full, PARTIAL, or ZERO — cycling between them, so
 *     the two abnormal-looking-but-normal cases are always reachable;
 *   - the OTP alternates between a live 90-second code and `hasCode: false`, so
 *     the "no active code right now" state is not something you have to force.
 *
 * The pricing route is the one thing here with no backend counterpart yet — see
 * `cardPricingApi.ts`. It is mocked so the screens can be developed, and its
 * absence in production is what makes them refuse to sell rather than guess.
 */

const envelope = <T>(result: T) => ({ isSuccess: true, error: null, errorCode: null, result });

function fail(errorCode: string, error: string, status = 400) {
  return HttpResponse.json({ isSuccess: false, error, errorCode, result: null }, { status });
}

const SETTLE_AFTER_MS = 20_000;
const OTP_TTL_MS = 90_000;

/** Novix's own card ids. The partner's id is not modelled here because it never leaves the backend. */
interface DevCardRow {
  id: number;
  status: 'Issuing' | 'Active' | 'Closing' | 'Closed' | 'Failed' | 'Unresolved';
  maskedPan: string | null;
  balanceUsd: string | null;
  balanceCurrency: string | null;
  issuanceChargeUsdt: string;
  createdAt: string;
  issuedAt: string | null;
  closedAt: string | null;
  refundedUsdt: string | null;
  failureReason: string | null;
  /** Dev-only bookkeeping, stripped by `cardView`. */
  settleAt: number | null;
}

/**
 * The Novix tariff, as an operator would have it in the admin panel.
 *
 * These are the SEEDED values, not constants the UI may rely on. Editing them
 * here and reloading is how you check that the screens follow the API — which is
 * the same thing `cardPricing.test.tsx` asserts programmatically.
 */
const pricing = {
  issuanceFeeUsdt: '30.00',
  firstDepositUsdt: '10.00',
  totalUsdt: '40.00',
  rechargeFeePercent: '3',
  minimumRechargeUsdt: '10.00',
  maximumRechargeUsdt: '10000.00',
  cardCurrency: 'USD'
};

const devCardRows: DevCardRow[] = [
  {
    id: 4101,
    status: 'Active',
    maskedPan: '435678••••1234',
    balanceUsd: '124.50',
    balanceCurrency: 'USD',
    issuanceChargeUsdt: '50.000000',
    createdAt: '2026-05-02T09:12:00Z',
    issuedAt: '2026-05-02T09:14:00Z',
    closedAt: null,
    refundedUsdt: null,
    failureReason: null,
    settleAt: null
  },
  {
    // A card that closed with a PARTIAL refund. Normal, and seeded so the screen
    // that renders it is never a hypothetical.
    id: 4090,
    status: 'Closed',
    maskedPan: '435678••••9911',
    balanceUsd: null,
    balanceCurrency: null,
    issuanceChargeUsdt: '50.000000',
    createdAt: '2026-03-11T11:00:00Z',
    issuedAt: '2026-03-11T11:02:00Z',
    closedAt: '2026-04-01T08:00:00Z',
    refundedUsdt: '3.410000',
    failureReason: null,
    settleAt: null
  },
  {
    // And one that closed with a ZERO refund. Also normal.
    id: 4088,
    status: 'Closed',
    maskedPan: '435678••••4402',
    balanceUsd: null,
    balanceCurrency: null,
    issuanceChargeUsdt: '50.000000',
    createdAt: '2026-02-02T15:20:00Z',
    issuedAt: '2026-02-02T15:22:00Z',
    closedAt: '2026-02-28T10:00:00Z',
    refundedUsdt: '0.000000',
    failureReason: null,
    settleAt: null
  }
];

let cardIdCounter = 4200;
/** Cycles the closure outcome so all three are reachable without editing code. */
let closeOutcome = 0;

function nextCardId(): number {
  cardIdCounter += 1;
  return cardIdCounter;
}

/** Applies the passage of time. Called before every read. */
function settle(): void {
  const now = Date.now();

  for (const row of devCardRows) {
    if (row.settleAt === null || now < row.settleAt) continue;
    row.settleAt = null;

    if (row.status === 'Issuing') {
      row.status = 'Active';
      row.issuedAt = new Date().toISOString();
      row.maskedPan = `435678••••${String(row.id).slice(-4)}`;
      row.balanceUsd = '10.00';
      row.balanceCurrency = 'USD';
      continue;
    }

    if (row.status === 'Closing') {
      row.status = 'Closed';
      row.closedAt = new Date().toISOString();
      row.balanceUsd = null;
      row.balanceCurrency = null;
      // full → partial → zero, round and round.
      const outcomes = ['12.000000', '4.250000', '0.000000'];
      row.refundedUsdt = outcomes[closeOutcome % outcomes.length] ?? '0.000000';
      closeOutcome += 1;
    }
  }
}

/**
 * Projects a row to the shape `CardView` has.
 *
 * Strips `settleAt`, so the mock physically cannot return a field production
 * lacks — the same guard the saved-card mock uses.
 */
function cardView(row: DevCardRow) {
  return {
    id: row.id,
    status: row.status,
    maskedPan: row.maskedPan,
    balanceUsd: row.balanceUsd,
    balanceCurrency: row.balanceCurrency,
    issuanceChargeUsdt: row.issuanceChargeUsdt,
    createdAt: row.createdAt,
    issuedAt: row.issuedAt,
    closedAt: row.closedAt,
    refundedUsdt: row.refundedUsdt,
    failureReason: row.failureReason
  };
}

function findCard(
  params: Record<string, string | readonly string[] | undefined>
): DevCardRow | undefined {
  const raw = params['cardId'];
  const id = Number(Array.isArray(raw) ? raw[0] : raw);
  return devCardRows.find((row) => row.id === id);
}

/** The backend REQUIRES the header on issue and recharge and generates nothing. */
function requireIdempotency(request: Request) {
  const key = request.headers.get('Idempotency-Key');
  return key === null || key.trim() === ''
    ? fail('card_idempotency_key_required', 'An Idempotency-Key header is required.')
    : null;
}

const seenKeys = new Set<string>();

/** Accepted top-ups, so GET /recharges can report what POST /recharge started. */
const recharges: Record<string, unknown>[] = [];

export const devCardHandlers: HttpHandler[] = [
  /* ─────────────────────────────────────────────────────── pricing (see above) */
  http.get('*/api/v1/cards/pricing', () => HttpResponse.json(envelope(pricing))),

  /* ────────────────────────────────────────────────────────────────── the list */
  http.get('*/api/v1/cards', () => {
    settle();
    return HttpResponse.json(envelope(devCardRows.map(cardView)));
  }),

  http.get('*/api/v1/cards/:cardId', ({ params }) => {
    settle();
    const row = findCard(params);
    // Somebody else's card and a card that does not exist are ONE answer, 404.
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);
    return HttpResponse.json(envelope(cardView(row)));
  }),

  /* ───────────────────────────────────────────────────────────────────── issue */
  http.post('*/api/v1/cards', ({ request }) => {
    const missingKey = requireIdempotency(request);
    if (missingKey !== null) return missingKey;

    const key = request.headers.get('Idempotency-Key') ?? '';
    if (seenKeys.has(key)) {
      // A REPLAY of the same submission answers the same card, as the backend's
      // idempotency does — it does not charge twice and it does not error.
      const existing = devCardRows.find((row) => row.status === 'Issuing');
      if (existing !== undefined) return HttpResponse.json(envelope(cardView(existing)));
    }
    seenKeys.add(key);

    const row: DevCardRow = {
      id: nextCardId(),
      // ACCEPTED. Not issued. The card becomes Active 20 seconds from now.
      status: 'Issuing',
      maskedPan: null,
      balanceUsd: null,
      balanceCurrency: null,
      issuanceChargeUsdt: new Decimal(pricing.totalUsdt).toFixed(6),
      createdAt: new Date().toISOString(),
      issuedAt: null,
      closedAt: null,
      refundedUsdt: null,
      failureReason: null,
      settleAt: Date.now() + SETTLE_AFTER_MS
    };

    devCardRows.unshift(row);
    return HttpResponse.json(envelope(cardView(row)));
  }),

  /* ────────────────────────────────────────────────────────────────── recharge */
  http.post('*/api/v1/cards/:cardId/recharge', async ({ params, request }) => {
    settle();

    const missingKey = requireIdempotency(request);
    if (missingKey !== null) return missingKey;

    const row = findCard(params);
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);
    if (row.status !== 'Active') {
      return fail('card_not_active', 'That card is not active.', 409);
    }

    const body = (await request.json()) as { amountUsdt?: string };
    const amount = new Decimal(body.amountUsdt ?? '0');

    if (
      amount.lessThan(pricing.minimumRechargeUsdt) ||
      amount.greaterThan(pricing.maximumRechargeUsdt)
    ) {
      return fail(
        'card_amount_out_of_range',
        'That amount is outside the limits for a card top-up.'
      );
    }

    const fee = amount.times(pricing.rechargeFeePercent).dividedBy(100);
    const total = amount.plus(fee);
    const rate = new Decimal('1.0000');

    // ACCEPTED: `Reserved`. Kept so GET /recharges can answer, and so a top-up in
    // dev settles the way it does in production — later, and observably.
    const accepted = {
      id: nextCardId(),
      cardId: row.id,
      status: 'Reserved',
      amountUsdt: amount.toFixed(6),
      feeUsdt: fee.toFixed(6),
      totalDebitUsdt: total.toFixed(6),
      targetAmount: amount.times(rate).toFixed(2),
      targetCurrency: pricing.cardCurrency,
      rate: rate.toFixed(4),
      createdAt: new Date().toISOString(),
      completedAt: null,
      failureReason: null
    };

    recharges.push(accepted);

    return HttpResponse.json(envelope(accepted));
  }),

  /* ─────────────────────────────────────────────────────────────── recharges */
  http.get('*/api/v1/cards/:cardId/recharges', ({ params }) => {
    settle();

    const row = findCard(params);
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);

    return HttpResponse.json(
      envelope(
        recharges
          .filter((recharge) => recharge.cardId === row.id)
          .slice()
          .reverse()
      )
    );
  }),

  /* ───────────────────────────────────────────────────────────────────── close */
  http.post('*/api/v1/cards/:cardId/close', ({ params }) => {
    settle();

    const row = findCard(params);
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);

    if (row.status === 'Closing' || row.status === 'Closed') {
      return fail('card_already_closing', 'That card is already being closed.', 409);
    }
    if (row.status !== 'Active') {
      return fail('card_not_active', 'That card is not active.', 409);
    }

    // No money moves. `refundedUsdt` stays null: nobody knows the amount yet.
    row.status = 'Closing';
    row.refundedUsdt = null;
    row.settleAt = Date.now() + SETTLE_AFTER_MS;

    return HttpResponse.json(envelope(cardView(row)));
  }),

  /* ─────────────────────────────────────────────────────────────────────── otp */
  http.get('*/api/v1/cards/:cardId/otp', ({ params }) => {
    settle();

    const row = findCard(params);
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);
    if (row.status !== 'Active') return fail('card_not_active', 'That card is not active.', 409);

    // Alternates so the "no active code" state is always one press away. Both
    // answers are successes — 204 from the partner is not an error.
    const hasCode = Math.floor(Date.now() / 30_000) % 3 !== 2;

    return HttpResponse.json(
      envelope(
        hasCode
          ? {
              code: '481920',
              expiresAt: new Date(Date.now() + OTP_TTL_MS).toISOString(),
              hasCode: true
            }
          : { code: null, expiresAt: null, hasCode: false }
      )
    );
  }),

  /* ───────────────────────────────────────────────────────────────── sensitive */
  http.get('*/api/v1/cards/:cardId/sensitive', ({ params }) => {
    settle();

    const row = findCard(params);
    if (row === undefined) return fail('card_not_found', 'No such card.', 404);
    if (row.status !== 'Active') return fail('card_not_active', 'That card is not active.', 409);

    return HttpResponse.json(
      envelope({
        pan: '4356781234561234',
        cvv: '731',
        expiry: '04/29',
        expiryMonth: 4,
        expiryYear: 29,
        holderName: 'NOVIX CARDHOLDER',
        billingCountry: 'Singapore',
        billingCity: 'Singapore',
        billingAddress: '10 Anson Road, #22-02',
        billingPostCode: '079903'
      })
    );
  })
];
